import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { 
  Menu, X, User, LogOut, Heart, LayoutDashboard, ChevronDown, Bell,
  Clapperboard, Users, Briefcase, Building2, Wrench, Calendar, CalendarDays,
  BookOpen, CreditCard, Info, HeadphonesIcon, MessageSquare, LifeBuoy, HelpCircle,
  Palette, Sun, Moon, Monitor, Check, Clock, Search, Plus, Camera, FolderKanban, ShieldCheck,
  ListChecks, Star, Cookie
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import SiteLogo from './ui/SiteLogo';
import { ThemeToggle } from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import { useRecentPaths } from '../hooks/useRecentPaths';

import { isInCfCooldown } from '../api/apiClient';
import { getMobileDrawer } from '../config/navigation';
import {
  POST_CASTING_ROUTE,
  canShowPostCastingCta,
  trackPostCastingCta,
} from '../lib/postCastingCta';

/**
 * Map a recent path back to a friendly menu label for the profile dropdown.
 * Examples: `/dashboard?tab=settings` → "Dashboard · Settings"
 *           `/messages` → "Messages"
 */
function prettifyRecentLabel(p) {
  try {
    const [base, query = ''] = p.split('?');
    const params = new URLSearchParams(query);
    const tab = params.get('tab');
    const segments = base.split('/').filter(Boolean);
    const root = segments[0] || 'home';
    const rootLabel = root.charAt(0).toUpperCase() + root.slice(1).replace(/-/g, ' ');
    if (tab) return `${rootLabel} · ${tab.charAt(0).toUpperCase() + tab.slice(1)}`;
    if (segments.length > 1) {
      return `${rootLabel} · ${segments[segments.length - 1].slice(0, 18)}`;
    }
    return rootLabel;
  } catch {
    return p;
  }
}
import { NotificationsDropdown } from './NotificationsDropdown';
import MessagesDropdown from './MessagesDropdown';
import ReportIssueModal from './support/ReportIssueModal';
import { toast } from 'sonner';
import { getTourIdsForPath } from './onboarding/onboardingContent';

const DEFAULT_LOGO_URL = "https://my.castingcalls.uk/storage/2025/03/ccuk-logo-shadow.png";
const DEFAULT_SITE_NAME = "Casting Calls UK";

// Generate a human-readable message from admin notification event_type + metadata
function getAdminNotifMessage(notif) {
  const { event_type, metadata } = notif;
  if (event_type === 'user_registered') return `New user registered: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_archived') return `${metadata?.count || 1} user(s) archived`;
  if (event_type === 'user_unarchived') return `User unarchived: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_bulk_deleted') return `${metadata?.count || 1} user(s) deleted`;
  if (event_type === 'user_upgraded') return `User upgraded: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'user_downgraded') return `User downgraded: ${metadata?.email || 'Unknown'}`;
  if (event_type === 'payment_failed') return `Payment failed for ${metadata?.email || 'a user'}`;
  if (event_type === 'inactivity_archive_run') return `Inactivity archive: ${metadata?.count || 0} users archived`;
  return event_type?.replace(/_/g, ' ') || 'New notification';
}

// Primary navigation items (always visible)
const PRIMARY_NAV = [
  { label: 'Casting Calls', path: '/casting-calls' },
  { label: 'Talent', path: '/talent' },
  { label: 'News', path: '/news' },
  { label: 'Pricing', path: '/pricing' },
];

const MEMBER_PRIMARY_NAV = [
  { label: 'Casting Calls', path: '/casting-calls' },
  { label: 'Talent', path: '/talent' },
  { label: 'Agents', path: '/agents' },
  { label: 'Featured', path: '/featured' },
];

// Discover mega menu items
const DISCOVER_MENU = {
  people: [
    { label: 'Casting Professionals', path: '/casting-professionals', icon: Clapperboard, description: 'Find and connect with UK casting directors' },
    { label: 'Agents', path: '/agents', icon: Briefcase, description: 'Browse talent agencies and representatives' },
    { label: 'Talent Directory', path: '/talent', icon: Users, description: 'Discover performers across the UK' },
    { label: 'Featured', path: '/featured', icon: Star, description: "Meet this month's standout industry members" },
  ],
  industry: [
    { label: 'Companies', path: '/companies', icon: Building2, description: 'Production companies and studios' },
    { label: 'Crew', path: '/crew', icon: Users, description: 'Directors, producers, cinematographers, and crew' },
    { label: 'Services', path: '/services', icon: Wrench, description: 'Industry services and suppliers' },
    { label: 'Events', path: '/events', icon: Calendar, description: 'Networking and industry events' },
  ]
};

// Resources mega menu items
const PLATFORM_MENU = [
  { label: 'How It Works', path: '/how-it-works', icon: BookOpen, description: 'Learn how CCUK connects the industry' },
  { label: 'Pricing', path: '/pricing', icon: CreditCard, description: 'View plans and features' },
  { label: 'About', path: '/about', icon: Info, description: 'Our story and mission' },
  { label: 'Support', path: '/support', icon: HeadphonesIcon, description: 'Help centre and contact' },
];

// Public mobile menu groups.
// Logged-out users need a marketing/site-map drawer, not the logged-in app drawer.
// Keep this separate from getMobileDrawer(user), which is role-aware and signed-in only.
const PUBLIC_MOBILE_NAV_GROUPS = [
  {
    id: 'explore',
    title: 'Explore',
    items: [
      { label: 'Casting Calls', path: '/casting-calls', icon: Search },
      { label: 'Talent Directory', path: '/talent', icon: Users },
      { label: 'Featured', path: '/featured', icon: Star },
      { label: 'Casting Professionals', path: '/casting-professionals', icon: Clapperboard },
      { label: 'Agents', path: '/agents', icon: Briefcase },
      { label: 'Production Companies', path: '/companies', icon: Building2 },
      { label: 'Industry Services', path: '/services', icon: Wrench },
      { label: 'Crew Directory', path: '/crew', icon: Camera },
      { label: 'Events', path: '/events', icon: Calendar },
      { label: 'News & Advice', path: '/news', icon: BookOpen },
    ],
  },
  {
    id: 'platform',
    title: 'Resources',
    items: [
      { label: 'How It Works', path: '/how-it-works', icon: BookOpen },
      { label: 'Pricing', path: '/pricing', icon: CreditCard },
      { label: 'About', path: '/about', icon: Info },
    ],
  },
  {
    id: 'support',
    title: 'Support',
    items: [
      { label: 'Support Centre', path: '/support', icon: HeadphonesIcon },
      { label: 'Contact', path: '/contact', icon: MessageSquare },
    ],
  },
  {
    id: 'legal',
    title: 'Legal',
    items: [
      { label: 'Terms of Service', path: '/terms', icon: BookOpen },
      { label: 'Privacy Policy', path: '/privacy', icon: ShieldCheck },
      { label: 'Cookie Policy', path: '/cookie-policy', icon: BookOpen },
      { label: 'Community Guidelines', path: '/guidelines', icon: Users },
    ],
  },
];

// Role display metadata (mirrors RoleSwitcher.js — kept local so the drawer
// can render role chips without reaching into the desktop component).
const ROLE_META = {
  talent: { label: 'Talent', Icon: Users },
  casting_professional: { label: 'Casting Director', Icon: Clapperboard },
  agent: { label: 'Agent', Icon: Briefcase },
  industry_service: { label: 'Industry Service', Icon: Wrench },
  production_company: { label: 'Production Company', Icon: Building2 },
  crew: { label: 'Crew', Icon: Camera },
  project_creator: { label: 'Project Creator', Icon: FolderKanban },
  admin: { label: 'Admin', Icon: ShieldCheck },
};

// Reusable collapsible section for the mobile drawer body. Single accordion
// pattern: only one section open at a time, controlled by the parent.
// Renders a hairline separator under each section to match the approved
// mockup's compact, premium feel.
function DrawerSection({ id, title, open, onToggle, children }) {
  return (
    <div
      className="mb-1.5 min-[390px]:mb-2 overflow-hidden rounded-2xl border border-[#7A1DD8]/12 dark:border-[#7AA2FF]/12 bg-white/70 dark:bg-white/[0.035] shadow-[0_10px_30px_-24px_rgba(122,29,216,0.45)]"
      data-testid={`${id}-section`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        className="w-full flex items-center justify-between px-3 py-2.5 min-[390px]:px-3.5 min-[390px]:py-3 hover:bg-[#7A1DD8]/[0.035] dark:hover:bg-[#7AA2FF]/[0.045] transition-colors"
        data-testid={`${id}-toggle`}
      >
        <h4 className="text-[10px] min-[390px]:text-[11px] font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-[0.16em]">{title}</h4>
        <ChevronDown
          className={`h-4 w-4 text-slate-500 dark:text-slate-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        id={`${id}-panel`}
        inert={!open ? true : undefined}
        aria-hidden={!open}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100 pb-2' : 'grid-rows-[0fr] opacity-0 pb-0'
        }`}
      >
        <div className="overflow-hidden px-1.5">{children}</div>
      </div>
    </div>
  );
}

// Mobile-specific Notifications Component with stable panel
function MobileNotifications({ notifications = [], unreadCount = 0, onClose }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  const closePanel = () => {
    setIsOpen(false);
  };

  // Get recent notifications (max 5)
  const displayNotifications = notifications.slice(0, 5);

  return (
    <div className="relative">
      {/* Bell Icon Button - Premium Style */}
      <button
        onClick={togglePanel}
        className="relative p-2.5 rounded-xl hover:bg-white/80 dark:hover:bg-white/10 transition-all duration-300 backdrop-blur-sm border border-transparent hover:border-theme/10"
        aria-label="Toggle notifications"
      >
        <Bell className="h-5 w-5 text-theme-text" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-gradient-to-br from-[#002FA7] to-[#0055FF] flex items-center justify-center text-[10px] font-bold text-white border-2 border-white dark:border-[#0B1120] shadow-lg animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Premium Notification Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-[140]"
            onClick={closePanel}
          />
          
          {/* Panel — Constrained INSIDE the mobile drawer column so it
              never overflows horizontally. Drawer width is
              `min(88vw, 360px)`, profile card padding eats ~24px, so we
              cap at 18rem (288px) and use `min(calc(88vw-4rem), 18rem)`
              to ensure the panel always sits inside the drawer on
              every viewport (incl. 320px small phones). */}
          <div className="absolute right-0 top-[calc(100%+8px)] w-[min(calc(94vw-3.5rem),20rem)] bg-white dark:bg-[#0D1528] rounded-2xl shadow-2xl border border-theme/10 z-[145] max-h-[480px] flex flex-col overflow-hidden">
            {/* Header - Premium gradient */}
            <div className="relative px-3 sm:px-5 py-3 sm:py-4 border-b border-theme/10 bg-gradient-to-br from-[#002FA7]/5 via-transparent to-transparent">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#002FA7] via-[#0055FF] to-[#002FA7]" />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-[#002FA7]" />
                  <h3 className="text-sm font-bold text-theme-text tracking-tight">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-gradient-to-r from-[#002FA7] to-[#0055FF] text-white">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <button
                  onClick={closePanel}
                  className="p-1.5 rounded-lg hover:bg-theme-hover transition-all duration-200 text-theme-muted hover:text-theme-text"
                  aria-label="Close notifications"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Notifications List - Scrollable */}
            <div className="overflow-y-auto flex-1 p-2 sm:p-3">
              {displayNotifications.length > 0 ? (
                <div className="space-y-2">
                  {displayNotifications.map((notif, idx) => (
                    <button
                      key={notif.id || idx}
                      onClick={() => {
                        // Navigate to notification or mark as read
                        if (notif.link) {
                          window.location.href = notif.link;
                        }
                        closePanel();
                      }}
                      className={`w-full text-left p-2.5 sm:p-3.5 rounded-xl transition-all duration-300 group ${
                        notif.read
                          ? 'bg-theme-surface/20 hover:bg-theme-surface/40'
                          : 'bg-gradient-to-br from-[#002FA7]/10 to-[#0055FF]/5 hover:from-[#002FA7]/15 hover:to-[#0055FF]/10 border border-[#002FA7]/20 shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        {/* Unread indicator */}
                        {!notif.read && (
                          <div className="w-2 h-2 bg-gradient-to-br from-[#002FA7] to-[#0055FF] rounded-full mt-1.5 flex-shrink-0 animate-pulse" />
                        )}
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="text-sm font-semibold text-theme-text group-hover:text-[#002FA7] transition-colors duration-200 line-clamp-1">
                              {notif.message || notif.title || 'Notification'}
                            </div>
                            {notif.created_at && (
                              <div className="text-[10px] text-theme-muted whitespace-nowrap">
                                {new Date(notif.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-theme-muted line-clamp-2 leading-relaxed">
                            {notif.message || 'You have a new notification'}
                          </div>
                        </div>
                        
                        {/* Arrow indicator on hover */}
                        <svg 
                          className="w-4 h-4 text-[#002FA7] opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0 mt-1"
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 px-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#002FA7]/10 to-[#0055FF]/10 flex items-center justify-center mx-auto mb-3">
                    <Bell className="h-7 w-7 text-[#002FA7]/40" />
                  </div>
                  <p className="text-sm font-medium text-theme-text mb-1">No notifications yet</p>
                  <p className="text-xs text-theme-muted">We'll notify you when something arrives</p>
                </div>
              )}
            </div>

            {/* Footer - View all notifications */}
<div className="border-t border-theme/10 p-2 sm:p-3 bg-gradient-to-b from-transparent to-slate-50/50 dark:to-[#0B1120]/50">
  <button
    type="button"
    onClick={() => {
      closePanel();
      onClose?.();
      navigate('/notifications');
    }}
    className="w-full py-2.5 px-4 rounded-xl text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7]/10 transition-all duration-200 flex items-center justify-center gap-1.5 group"
  >
    View all notifications
    <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
    </svg>
  </button>
</div>
          </div>
        </>
      )}
    </div>
  );
}

export function Header() {
  const { user, logout, refreshUser, loading: authLoading, isLoggingOut, userRoles, activeRole, switchRole } = useAuth();
  const { themePreference, setThemePreference } = useTheme();
  const { branding, brandingLoaded } = useBranding();
  const mobileSiteName = branding?.site_name || DEFAULT_SITE_NAME;
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Mobile drawer search query (Phase 3 redesign) — filters menu items
  // by label across every section while the drawer is open.
  const [drawerSearch, setDrawerSearch] = useState('');
  // Mobile drawer — which Tier-2 section is expanded.
  // Single-open accordion: all sections start closed, and opening one
  // drawer section closes the others. A Set is kept to minimise the
  // surrounding render changes, but it only ever contains zero or one id.
  const [mobileOpenSections, setMobileOpenSections] = useState(() => new Set());
  // Premium mobile role-switch bottom sheet. Declared with the rest of
  // the mobile drawer state because effects below reference it in their
  // dependency arrays.
  const [roleSheetOpen, setRoleSheetOpen] = useState(false);
  const [mobileThemeSwitching, setMobileThemeSwitching] = useState(false);
  const [discoverMenuOpen, setDiscoverMenuOpen] = useState(false);
  const [platformMenuOpen, setPlatformMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [messageUnread, setMessageUnread] = useState(0);
  const discoverCloseTimeout = useRef(null);
  const platformCloseTimeout = useRef(null);
  const mobileThemeSwitchTimer = useRef(null);
  const mobileDrawerTouchStartRef = useRef(null);
  const { recent: recentPaths } = useRecentPaths();

  // App-shell paths get a trimmed header (no marketing nav, no mega-menus).
  // Logged-in users on these paths only need: logo, search shortcut, theme,
  // notifications, messages, profile menu. Keeps the in-app UI focused.
  const APP_SHELL_PREFIXES = ['/dashboard', '/messages', '/account', '/saved', '/admin', '/profile/edit', '/my-listings'];
  
  const isCurrentlyOnAppShellPath = APP_SHELL_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  // Preserve the previous app-shell/header mode during logout even if 
  // the route changes to /, then release it after logout completes. 
  // This prevents the post-logout header "bounce" (layout shift) if the 
  // logout call hasn't resolved yet but the app has already navigated 
  // away from a dashboard/workspace route.
  const wasAppShell = useRef(false);
  useEffect(() => {
    if (!isLoggingOut) {
      wasAppShell.current = isCurrentlyOnAppShellPath && !!user;
    }
  }, [isCurrentlyOnAppShellPath, user, isLoggingOut]);

  const isAppShell = (!!user || isLoggingOut) && (
    isCurrentlyOnAppShellPath || (isLoggingOut && wasAppShell.current)
  );

  // Derive profile image from user data
  const profileImage =
  user?.avatar_url ||
  user?.headshot_url ||
  user?.profile_image ||
  user?.profile_image_url ||
  user?.headshot ||
  user?.headshotUrl ||
  user?.profile_photo_url ||
  user?.photo_url ||
  user?.logo_url ||
  user?.avatar ||
  user?.talent_profile?.headshot_url ||
  user?.talent_profile?.profile_image ||
  user?.talent_profile?.profile_image_url ||
  user?.profile?.headshot_url ||
  user?.profile?.profile_image ||
  user?.profile?.profile_image_url ||
  null;

  // Display name — mirrors the resolution logic used by
  // DashboardHeroShell so the mobile drawer header and the dashboard
  // hero/greeting always show the same name. Falls back through
  // display_name → agency / business / company name → user.name → 'Member'.
  const resolvedDisplayName = (
    user?.profile?.display_name ||
    user?.profile?.agency_name ||
    user?.profile?.business_name ||
    user?.profile?.company_name ||
    user?.display_name ||
    user?.name ||
    'Member'
  );

  const userInitial = (
  resolvedDisplayName?.charAt(0) ||
  user?.email?.charAt(0) ||
  'U'
).toUpperCase();
const [profileImageFailed, setProfileImageFailed] = useState(false);

useEffect(() => {
  setProfileImageFailed(false);
}, [profileImage]);

const hasProfileImage = Boolean(profileImage) && !profileImageFailed;
  // IMPORTANT: the real source of truth for the bell badge is the backend's
  // canonical `unread_count` — the `notifications` array is only a 30-item
  // window and filtering it client-side under-counts when the user has more
  // than 30 unread items (which was the original "9+ that never clears" bug).
  const unreadCount = unreadNotifications;

  // Fetch notifications for mobile menu — admin-aware
  useEffect(() => {
    if (!user) return;
    const isAdmin = user.active_role === 'admin' || user.role === 'admin';
    const API_URL = window.__BACKEND_URL__;

    const fetchNotifications = async () => {
      // Defer to NotificationsDropdown — if a CF/origin error happened
      // recently the bell will refresh us via `notifications:changed`
      // when the storm clears.
      if (isInCfCooldown()) return;
      try {
        if (isAdmin) {
          const { data } = await axios.get(`${API_URL}/api/admin/notifications?limit=30`, { withCredentials: true });
          const notifs = (data.notifications || []).map(n => ({
            ...n,
            message: n.message || getAdminNotifMessage(n),
          }));
          setNotifications(notifs);
          setUnreadNotifications(typeof data?.unread_count === 'number' ? data.unread_count : 0);
        } else {
          const { data } = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
          setNotifications(data.notifications || []);
          setUnreadNotifications(typeof data?.unread_count === 'number' ? data.unread_count : 0);
        }
      } catch {
        // Silent fail
      }
    };

    const fetchMessageStats = async () => {
      if (isInCfCooldown()) return;
      try {
        const { data } = await axios.get(`${API_URL}/api/messages/stats`, { withCredentials: true });
        setMessageUnread(data.total_badge_count || 0);
      } catch {
        // Silent fail
      }
    };

    fetchNotifications();
    fetchMessageStats();
    // 180s heartbeat — NotificationsDropdown polls at 60s and dispatches
    // `notifications:changed` so we don't need a tight tick here; this
    // is purely a safety net for mobile-nav badges if the bell isn't
    // mounted on the current route.
    const interval = setInterval(() => { fetchNotifications(); fetchMessageStats(); }, 180000);

    // Refresh when admin actions happen or notifications change elsewhere
    const handleRefresh = () => fetchNotifications();
    window.addEventListener('admin:stats-refresh', handleRefresh);
    window.addEventListener('admin:notifications-changed', handleRefresh);
    window.addEventListener('notifications:changed', handleRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('admin:stats-refresh', handleRefresh);
      window.removeEventListener('admin:notifications-changed', handleRefresh);
      window.removeEventListener('notifications:changed', handleRefresh);
    };
  }, [user]);

  // Listen for profile updates from profile editors
  useEffect(() => {
    const handleProfileUpdate = async (e) => {
      // Refresh user data from auth context to sync everywhere
      if (refreshUser) {
        await refreshUser();
      }
    };
    window.addEventListener('profileUpdated', handleProfileUpdate);
    window.addEventListener('profileImageUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
      window.removeEventListener('profileImageUpdated', handleProfileUpdate);
    };
  }, [refreshUser]);

  // Close mobile menu and role sheet on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setRoleSheetOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      // Every mobile menu section starts collapsed.
      setMobileOpenSections(new Set());
      setDrawerSearch('');
    }
  }, [mobileMenuOpen]);

  // Lock body scroll when mobile menu OR role sheet is open
  useEffect(() => {
    if (mobileMenuOpen || roleSheetOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen, roleSheetOpen]);

  // Mobile drawer theme switch polish:
  // Temporarily suppress colour transitions inside the drawer while the
  // theme flips. Without this, the drawer background changes immediately
  // but menu text using transition-colors can lag for a split second.
  const handleMobileThemeToggle = useCallback(() => {
    setMobileThemeSwitching(true);
    setThemePreference(themePreference === 'dark' ? 'light' : 'dark');

    if (mobileThemeSwitchTimer.current) {
      clearTimeout(mobileThemeSwitchTimer.current);
    }

    mobileThemeSwitchTimer.current = setTimeout(() => {
      setMobileThemeSwitching(false);
      mobileThemeSwitchTimer.current = null;
    }, 260);
  }, [setThemePreference, themePreference]);

  useEffect(() => {
    return () => {
      if (mobileThemeSwitchTimer.current) {
        clearTimeout(mobileThemeSwitchTimer.current);
      }
    };
  }, []);

  // Toggle body class when impersonating so main content offsets for banner
  useEffect(() => {
    if (user?.impersonator) {
      document.body.classList.add('is-impersonating');
    } else {
      document.body.classList.remove('is-impersonating');
    }
    return () => document.body.classList.remove('is-impersonating');
  }, [user?.impersonator]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const closeMobile = useCallback(() => {
    setMobileMenuOpen(false);
    // Clear transient state; the next open starts collapsed.
    setMobileOpenSections(new Set());
    // Clear the drawer search query so the next open starts fresh
    setDrawerSearch('');
  }, []);

  const handleMobileDrawerTouchStart = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    mobileDrawerTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  }, []);

  const handleMobileDrawerTouchEnd = useCallback((event) => {
    const start = mobileDrawerTouchStartRef.current;
    const touch = event.changedTouches?.[0];
    mobileDrawerTouchStartRef.current = null;

    if (!start || !touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    const horizontalEnough = deltaX >= 72;
    const primarilyHorizontal =
      Math.abs(deltaX) > Math.abs(deltaY) * 1.25;

    if (horizontalEnough && primarilyHorizontal) {
      closeMobile();
    }
  }, [closeMobile]);

  const [showReportIssue, setShowReportIssue] = useState(false);

  // "Show help" — resets the onboarding flags (intro panel + tour) for the
  // current page only, so the intro/tour reappears without nuking the rest
  // of the user's dismissed-state across the app.
  const handleReplayHelp = useCallback(() => {
    const activeRole = user?.active_role || user?.role;
    const tourIds = getTourIdsForPath(location.pathname, activeRole);
    if (!tourIds.length) {
      toast.info("No help panel is registered for this page yet.");
      return;
    }
    try {
      const uid = user?.id || 'anon';
      tourIds.forEach((tid) => {
        window.localStorage.removeItem(`onboarding:${uid}:${tid}_intro_dismissed`);
        window.localStorage.removeItem(`onboarding:${uid}:${tid}_tour_completed`);
      });
    } catch { /* private mode — ignore */ }
    window.dispatchEvent(new CustomEvent('onboarding:reset', { detail: { tourIds, pathname: location.pathname } }));
    toast.success("Help refreshed — the intro will reappear on this page.");
  }, [user, location.pathname]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      {/* Impersonation banner — only shown when an admin is impersonating another user */}
      {user?.impersonator && (
        <div
          className="fixed top-0 left-0 right-0 z-[130] bg-amber-500 text-black shadow-md h-9"
          data-testid="impersonation-banner"
          role="alert"
        >
          <div className="h-full px-3 sm:px-4 flex items-center justify-between gap-2 sm:gap-3 text-xs sm:text-sm font-medium">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span role="img" aria-label="warning" className="flex-shrink-0">⚠️</span>
              <span className="truncate">
                <span className="hidden sm:inline">Logged in as </span>
                <strong>{user?.name}</strong>
                <span className="hidden sm:inline"> — original session: <strong>{user.impersonator.name || user.impersonator.email}</strong></span>
              </span>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const axios = (await import('axios')).default;
                  const API_URL = window.__BACKEND_URL__;
                  const { data } = await axios.post(`${API_URL}/api/auth/stop-impersonating`, {}, { withCredentials: true });
                  // Mirror the impersonate flow — backend swaps the
                  // httpOnly cookie AND returns the fresh admin token in
                  // the body. Update the localStorage bearer cache so
                  // the apiClient interceptor stops sending the stale
                  // impersonated token on the very next request.
                  const newToken = data?.access_token || data?.token;
                  if (newToken) {
                    try { localStorage.setItem('access_token', newToken); } catch { /* ignore */ }
                  } else {
                    try { localStorage.removeItem('access_token'); } catch { /* ignore */ }
                  }
                  window.location.href = '/dashboard';
                } catch (e) {
                  // eslint-disable-next-line no-alert
                  alert('Failed to exit impersonation');
                }
              }}
              className="flex-shrink-0 whitespace-nowrap px-2.5 sm:px-3 py-1 rounded-md bg-black/90 text-white text-xs font-semibold hover:bg-black transition-colors"
              data-testid="exit-impersonation-btn"
              aria-label="Exit impersonation and return to admin"
            >
              <span className="hidden sm:inline">Exit impersonation</span>
              <span className="sm:hidden">Exit</span>
            </button>
          </div>
        </div>
      )}
      <header
        className={`fixed left-0 right-0 z-[100] border-b border-theme ${user?.impersonator ? 'top-9' : 'top-0'}`}
        data-testid="header"
        style={{ backgroundColor: 'var(--theme-app-header, var(--theme-bg-secondary))' }}
      >
        <div className="max-w-screen-2xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-[72px] flex items-center justify-between xl:grid xl:grid-cols-[minmax(210px,1fr)_auto_minmax(210px,1fr)] xl:gap-4">
          {/* Logo — dark-mode aware via BrandingContext.
              Mobile deliberately shows the logo mark only; desktop retains
              the full CCUK wordmark/tagline treatment. */}
          <Link
            to="/"
            className="flex items-center flex-shrink-0 min-w-0 xl:justify-self-start [&_img]:transition-transform [&_svg]:transition-transform max-[639px]:[&_img]:origin-left max-[639px]:[&_svg]:origin-left max-[639px]:[&_img]:scale-[1.14] max-[639px]:[&_svg]:scale-[1.14]"
            data-testid="logo-link"
            aria-label={mobileSiteName}
          >
            <SiteLogo variant="header" />
          </Link>

          {/* Desktop Navigation — centred on desktop for both logged-out and logged-in users.
              Mobile/tablet continue to use the drawer. During auth loading/logout we keep
              it hidden to avoid a route/header flash while session state settles. */}
          {!authLoading && !isLoggingOut && (
          <nav className="hidden xl:flex items-center justify-center gap-1 justify-self-center" data-testid="desktop-nav">
            {/* Primary nav items */}
            {(user ? MEMBER_PRIMARY_NAV : PRIMARY_NAV).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                  isActive(item.path)
                    ? 'bg-theme-primary text-white shadow-md'
                    : 'text-theme-text-secondary hover:text-theme-primary hover:bg-theme-primary/10'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.label}
              </Link>
            ))}

            {/* Discover Mega Menu */}
            <div 
              className="relative"
              onMouseEnter={() => {
                if (discoverCloseTimeout.current) {
                  clearTimeout(discoverCloseTimeout.current);
                }
                setDiscoverMenuOpen(true);
              }}
              onMouseLeave={() => {
                discoverCloseTimeout.current = setTimeout(() => {
                  setDiscoverMenuOpen(false);
                }, 150);
              }}
            >
              <button
                className={`px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                  discoverMenuOpen
                    ? 'bg-theme-primary/10 text-theme-primary'
                    : 'text-theme-text-secondary hover:text-theme-primary hover:bg-theme-primary/10'
                }`}
              >
                Discover
                <ChevronDown className="w-3 h-3" />
              </button>
              
              {/* Transparent bridge */}
              {discoverMenuOpen && (
                <div className="absolute top-full left-0 w-full h-2 bg-transparent" />
              )}
              
              {discoverMenuOpen && (
                <div className="absolute top-full left-0 mt-2 bg-theme-secondary border border-theme shadow-lg rounded-xl p-6 w-[500px] transition-opacity duration-150 opacity-100 z-50">
                  <div className="grid grid-cols-2 gap-6">
                    {/* People column */}
                    <div>
                      <h4 className="text-xs font-semibold text-theme-text uppercase tracking-wider mb-3">People</h4>
                      <div className="space-y-1">
                        {DISCOVER_MENU.people.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-theme-hover transition-all duration-200 group"
                          >
                            <item.icon className="w-4 h-4 text-theme-muted group-hover:text-theme-primary mt-0.5 flex-shrink-0 transition-colors duration-200" />
                            <div>
                              <div className="text-sm font-medium text-theme-text group-hover:text-theme-primary transition-colors duration-200">{item.label}</div>
                              <div className="text-xs text-theme-muted">{item.description}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Industry column */}
                    <div>
                      <h4 className="text-xs font-semibold text-theme-text uppercase tracking-wider mb-3">Industry</h4>
                      <div className="space-y-1">
                        {[...DISCOVER_MENU.industry, ...(user ? [{ label: 'News & Advice', path: '/news', icon: BookOpen, description: 'Industry news and practical advice' }, { label: 'Pricing', path: '/pricing', icon: CreditCard, description: 'Compare membership plans' }] : [])].map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-theme-hover transition-all duration-200 group"
                          >
                            <item.icon className="w-4 h-4 text-theme-muted group-hover:text-theme-primary mt-0.5 flex-shrink-0 transition-colors duration-200" />
                            <div>
                              <div className="text-sm font-medium text-theme-text group-hover:text-theme-primary transition-colors duration-200">{item.label}</div>
                              <div className="text-xs text-theme-muted">{item.description}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Resources Mega Menu */}
            <div 
              className="relative"
              onMouseEnter={() => {
                if (platformCloseTimeout.current) {
                  clearTimeout(platformCloseTimeout.current);
                }
                setPlatformMenuOpen(true);
              }}
              onMouseLeave={() => {
                platformCloseTimeout.current = setTimeout(() => {
                  setPlatformMenuOpen(false);
                }, 150);
              }}
            >
              <button
                className="px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-theme-text-secondary hover:text-theme-text hover:bg-theme-hover flex items-center gap-1"
              >
                Resources
                <ChevronDown className="w-3 h-3" />
              </button>
              
              {/* Transparent bridge */}
              {platformMenuOpen && (
                <div className="absolute top-full left-0 w-full h-2 bg-transparent" />
              )}
              
              {platformMenuOpen && (
                <div className="absolute top-full left-0 mt-2 bg-theme-secondary border border-theme shadow-lg rounded-xl p-6 w-[280px] transition-opacity duration-150 opacity-100 z-50">
                  <div className="space-y-1">
                    {PLATFORM_MENU.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-theme-hover transition-all duration-200 group"
                      >
                        <item.icon className="w-4 h-4 text-theme-muted group-hover:text-theme-primary mt-0.5 flex-shrink-0 transition-colors duration-200" />
                        <div>
                          <div className="text-sm font-medium text-theme-text group-hover:text-theme-primary transition-colors duration-200">{item.label}</div>
                          <div className="text-xs text-theme-muted">{item.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>
          )}

          {/* Right Side */}
          <div className="flex items-center gap-2 flex-shrink-0 xl:justify-self-end xl:justify-end xl:min-w-[220px] 2xl:min-w-[420px]">
            {/* Theme Toggle - Desktop only */}
            <div className="hidden xl:block">
              <ThemeToggle />
            </div>

            {authLoading ? (
              /* Stable placeholder matching final header right-side dimensions.
                 Reserves the SAME footprint as the logged-in layout
                 (search 220px + 2 icon buttons 36px + user dropdown ~110px)
                 so the header doesn't snap when auth resolves.
                 On widths BELOW xl the desktop skeleton is hidden by
                 `xl:flex`; the mobile placeholder below reserves space
                 for the hamburger button (40px) so we never briefly
                 render the logged-out Sign In / Join Now CTAs before
                 auth resolves — that was the source of the hover/refresh
                 flicker users were seeing. */
              <>
                <div className="hidden xl:flex items-center gap-2.5">
                  <div className="h-10 w-[240px] rounded-md bg-theme-secondary animate-pulse" />
                  <div className="h-10 w-10 rounded-full bg-theme-secondary animate-pulse" />
                  <div className="h-10 w-10 rounded-full bg-theme-secondary animate-pulse" />
                  <div className="h-10 w-[120px] rounded-lg bg-theme-secondary animate-pulse" />
                </div>
                <div
                  className="xl:hidden h-10 w-10 rounded-lg bg-theme-secondary animate-pulse"
                  aria-hidden="true"
                />
              </>
            ) : user ? (
              <>
                {/* Desktop: Full user dropdown menu */}
                <div className="hidden xl:flex items-center gap-3">
                  {canShowPostCastingCta(activeRole) && (
                    <Link
                      to={POST_CASTING_ROUTE}
                      onClick={() => trackPostCastingCta('header', activeRole)}
                      className="inline-flex"
                      data-testid="desktop-post-casting-cta"
                      aria-label="Post a Casting"
                    >
                      <Button className="h-10 px-4 gap-1.5 bg-theme-primary hover:bg-theme-primary-hover text-white font-semibold shadow-sm">
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        <span>Post a Casting</span>
                      </Button>
                    </Link>
                  )}

                  {/* Messages dropdown */}
                  <MessagesDropdown />

                  {/* Notifications */}
                  <NotificationsDropdown />
                  
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-theme-text gap-2 h-10" data-testid="user-menu-trigger">
                        {hasProfileImage ? (
                          <img 
  src={profileImage} 
  alt={user?.name || 'Profile photo'}
  className="h-8 w-8 rounded-full object-cover"
  onError={() => setProfileImageFailed(true)}
/>
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-theme-primary flex items-center justify-center">
                           <span className="text-xs font-semibold text-white">
  {userInitial}
</span>
                          </div>
                        )}
                        <span className="hidden sm:inline text-sm max-w-[100px] truncate">{user.name}</span>
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-theme-secondary border-theme z-[110]">
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} className="text-theme-text cursor-pointer" data-testid="menu-dashboard">
                        <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                      </DropdownMenuItem>
                      {canShowPostCastingCta(activeRole) && (
                        <DropdownMenuItem
                          onClick={() => {
                            trackPostCastingCta('profile_menu', activeRole);
                            navigate(POST_CASTING_ROUTE);
                          }}
                          className="text-theme-text cursor-pointer"
                          data-testid="menu-post-casting"
                          aria-label="Post a Casting"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Post a Casting
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => navigate('/dashboard?tab=connections')} className="text-theme-text cursor-pointer" data-testid="menu-network">
                        <Users className="h-4 w-4 mr-2" /> Network
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/messages')} className="text-theme-text cursor-pointer" data-testid="menu-messages">
                        <MessageSquare className="h-4 w-4 mr-2" /> Messages
                      </DropdownMenuItem>
                      {/* Planner — jumps into the dashboard with the Planner
                          tab pre-opened. Uses the same navigation state
                          contract as the mobile "Planner" quick-action so we
                          don't duplicate any routing logic. */}
                      <DropdownMenuItem
                        onClick={() => navigate('/dashboard', { state: { openPlanner: true } })}
                        className="text-theme-text cursor-pointer"
                        data-testid="menu-planner"
                      >
                        <CalendarDays className="h-4 w-4 mr-2" /> Planner
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/saved')} className="text-theme-text cursor-pointer" data-testid="menu-saved">
                        <Heart className="h-4 w-4 mr-2" /> Saved Items
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/account')} className="text-theme-text cursor-pointer" data-testid="menu-settings">
                        <User className="h-4 w-4 mr-2" /> Settings
                      </DropdownMenuItem>

                      {/* Recent — last few app pages, surfaced for quick re-entry */}
                      {recentPaths.length > 0 && (
                        <>
                          <DropdownMenuSeparator className="bg-theme/40" />
                          <div className="px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-theme-muted">
                            Recent
                          </div>
                          {recentPaths.slice(0, 4).map((r) => (
                            <DropdownMenuItem
                              key={r.path}
                              onClick={() => navigate(r.path)}
                              className="text-theme-muted hover:text-theme-text cursor-pointer text-xs"
                              data-testid={`menu-recent-${r.path}`}
                            >
                              <Clock className="h-3.5 w-3.5 mr-2 opacity-70" />
                              <span className="truncate">{prettifyRecentLabel(r.path)}</span>
                            </DropdownMenuItem>
                          ))}
                        </>
                      )}

                      {/* Appearance — three-way theme preference selector. The
                          header single-button toggle keeps its existing
                          light ↔ dark click behaviour; the explicit
                          "System default" option (follow OS) lives here so
                          users can opt into it without cluttering the header. */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger
                          className="text-theme-text cursor-pointer"
                          data-testid="menu-appearance-trigger"
                        >
                          <Palette className="h-4 w-4 mr-2" /> Appearance
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="min-w-[200px]">
                          {[
                            { key: 'light', label: 'Light Mode', Icon: Sun },
                            { key: 'dark', label: 'Dark Mode', Icon: Moon },
                            { key: 'system', label: 'System Default', Icon: Monitor },
                          ].map(({ key, label, Icon }) => (
                            <DropdownMenuItem
                              key={key}
                              onClick={() => setThemePreference(key)}
                              className="text-theme-text cursor-pointer flex items-center justify-between"
                              data-testid={`menu-theme-${key}`}
                            >
                              <span className="flex items-center">
                                <Icon className="h-4 w-4 mr-2" />
                                {label}
                              </span>
                              {themePreference === key && (
                                <Check className="h-4 w-4 text-[#002FA7]" />
                              )}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>

                      <DropdownMenuSeparator className="bg-theme-border" />
                      {/*
                        Support submenu — groups the three help actions into a
                        flyout so the top-level menu stays tidy. Each item
                        preserves its existing behaviour:
                          • Show help        → resets onboarding flags for this page
                          • Support Centre   → /support route (SupportCentrePage)
                          • Report an issue  → opens the existing ReportIssueModal
                      */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger
                          className="text-theme-text cursor-pointer"
                          data-testid="menu-support-trigger"
                        >
                          <LifeBuoy className="h-4 w-4 mr-2" /> Support
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="min-w-[200px]">
                          <DropdownMenuItem
                            onClick={handleReplayHelp}
                            className="text-theme-text cursor-pointer"
                            data-testid="menu-show-help"
                          >
                            <HelpCircle className="h-4 w-4 mr-2" /> Show help
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => navigate('/support')}
                            className="text-theme-text cursor-pointer"
                            data-testid="menu-support-centre"
                          >
                            <BookOpen className="h-4 w-4 mr-2" /> Support Centre
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setShowReportIssue(true)}
                            className="text-theme-text cursor-pointer"
                            data-testid="menu-report-issue"
                          >
                            <LifeBuoy className="h-4 w-4 mr-2" /> Report an issue
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuSeparator className="bg-theme-border" />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-400 cursor-pointer" data-testid="menu-logout">
                        <LogOut className="h-4 w-4 mr-2" /> Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Mobile: No CTA buttons - drawer handles everything */}
              </>
            ) : (
              /* Logged-out CTAs. The min-width matches the typical
                 logged-in dropdown footprint (≈220 px including avatar
                 + name) so the right side of the header doesn't snap
                 horizontally when the user logs out. Combined with the
                 brief authLoading skeleton re-armed inside the
                 AuthContext.logout(), this removes the post-logout
                 header bounce Wayne reported. */
              <div className="flex items-center justify-end gap-2 sm:min-w-[220px]" data-testid="logged-out-ctas">
                {/*
                 * Hide the Sign-In Link (the <a> element itself) on
                 * mobile — not just the button inside. Otherwise the
                 * <a> reserves ~44 px of layout width even with a
                 * display:none button, which was pushing the burger
                 * menu off-screen on ≤414-px viewports. The equivalent
                 * `hidden sm:flex` on the button alone is not enough.
                 * (Fix 2026-02-24, Wayne.)
                 */}
                <Link to="/login" className="hidden sm:inline-block">
                  <Button variant="ghost" className="text-theme-text h-9 text-sm" data-testid="signin-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-theme-primary hover:bg-theme-primary-hover text-white h-9 min-h-9 text-sm font-semibold px-3 sm:px-4" data-testid="join-btn">
                    Join Now
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button — hidden during authLoading so the
                two placeholders above don't visually compete with this
                button before auth resolves. */}
            {!authLoading && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="xl:hidden p-2 text-theme-text rounded-lg hover:bg-theme-hover transition-colors ml-1"
                data-testid="mobile-menu-btn"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer - Unified navigation experience */}
      <>
        {/* Backdrop Overlay - Above header */}
        <div
          className={`xl:hidden fixed inset-0 bg-black/40 dark:bg-black/60 z-[150] transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMobile}
        />
        
        {/* Drawer Panel - Premium Design - Above header.
            Uses full viewport width on very small phones (e.g. 320px)
            so the profile card, quick tiles and footer do not feel squeezed. */}
        <div
          className={`xl:hidden fixed top-0 right-0 bottom-0 w-screen max-w-[430px] sm:w-[400px] md:w-[430px] z-[160] shadow-2xl transform transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          data-testid="mobile-menu"
          onTouchStart={handleMobileDrawerTouchStart}
          onTouchEnd={handleMobileDrawerTouchEnd}
          style={{
            backgroundColor: 'var(--theme-app-sidebar, var(--theme-bg-secondary))',
            touchAction: 'pan-y',
          }}
        >
          <div className={`h-full flex flex-col overflow-hidden overscroll-contain backdrop-blur-sm ${mobileThemeSwitching ? '[&_*]:!transition-none' : ''}`}>
            
            {/* A. Integrated Social Header (Authenticated Only) */}
            {user && (
              <div
                className="relative z-20 flex-shrink-0 px-3 min-[390px]:px-4 sm:px-5 pt-[max(0.45rem,env(safe-area-inset-top))] pb-0 !rounded-none !border-0 !bg-transparent !shadow-none !ring-0"
                style={{ background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', outline: 'none' }}
                data-testid="mobile-profile-header"
                data-design-version="unboxed-mobile-header-v2"
              >
                {/* Slim CCUK accent line */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#002FA7] via-[#7A1DD8] to-[#002FA7]" />

                {/* Compact drawer close control.
                    The duplicate notification bell has been removed from the
                    drawer header because Notifications already has a quick
                    tile with a badge. This keeps the header cleaner and gives
                    the menu more usable vertical space. */}
                <button
                  onClick={closeMobile}
                  className="absolute right-2.5 min-[390px]:right-3 top-[max(0.15rem,env(safe-area-inset-top))] z-20 h-6 w-6 min-[390px]:h-7 min-[390px]:w-7 rounded-md text-theme-muted hover:text-[#002FA7] dark:hover:text-[#7AA2FF] hover:bg-[#002FA7]/[0.06] dark:hover:bg-[#0055FF]/[0.10] active:scale-95 transition-[background-color,color,transform] duration-200 ease-out flex items-center justify-center"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  title="Close menu"
                >
                  <svg
                    className="h-3.5 w-3.5 min-[390px]:h-4 min-[390px]:w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="4" y="5" width="16" height="14" rx="2" />
                    <path d="M10 5v14" />
                    <path d="M14 9l3 3-3 3" />
                  </svg>
                </button>

                {/* Connected identity layout: the avatar anchors the profile,
                    with the username above two aligned actions. View Profile
                    sits beside the role selector, not apart on the far edge.
                    Keep the surrounding drawer completely borderless. */}
                <div
                  className="relative z-20 mt-0 min-[390px]:mt-0 grid min-w-0 grid-cols-[64px_minmax(0,1fr)] min-[390px]:grid-cols-[70px_minmax(0,1fr)] items-end gap-x-3 px-1 min-[390px]:px-1.5 py-1.5 !rounded-none !border-0 !bg-transparent !shadow-none !ring-0"
                  style={{ background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', outline: 'none' }}
                  data-testid="mobile-profile-identity-grid"
                  data-design-version="mobile-profile-identity-grid-v11"
                >
                  <Link
                    to="/dashboard?tab=profile"
                    onClick={closeMobile}
                    className="group relative col-start-1 row-start-1 self-end rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002FA7]/50 focus-visible:ring-offset-2"
                    data-testid="mobile-profile-avatar"
                    aria-label="View profile"
                  >
                    {hasProfileImage ? (
                      <img
                        src={profileImage}
                        alt={resolvedDisplayName || 'Profile photo'}
                        className="h-16 w-16 min-[390px]:h-[70px] min-[390px]:w-[70px] rounded-lg object-cover ring-1 ring-black/10 dark:ring-white/15 shadow-sm transition-transform duration-150 group-hover:scale-[1.03] bg-theme-surface"
                        onError={() => setProfileImageFailed(true)}
                        loading="eager"
                        decoding="async"
                      />
                    ) : (
                      <div className="h-16 w-16 min-[390px]:h-[70px] min-[390px]:w-[70px] rounded-lg bg-gradient-to-br from-[#002FA7] to-[#7A1DD8] flex items-center justify-center ring-1 ring-black/10 dark:ring-white/15 shadow-sm transition-transform duration-150 group-hover:scale-[1.03]">
                        <span className="text-lg font-bold text-white tracking-tight">{userInitial}</span>
                      </div>
                    )}
                  </Link>

                  <div className="col-start-2 row-start-1 flex min-w-0 flex-col items-start justify-center">
                    <Link
                      to="/dashboard?tab=profile"
                      onClick={closeMobile}
                      className="block w-full min-w-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002FA7]/50"
                    >
                      <h3
                        className="relative top-2 truncate text-[15px] min-[390px]:text-base font-bold leading-[18px] tracking-tight text-theme-text"
                        title={resolvedDisplayName}
                        data-testid="mobile-profile-name"
                      >
                        {resolvedDisplayName}
                      </h3>
                    </Link>

                    {/* Compact actions sit together under the username. Do not stretch
                        the role wrapper or push View Profile to the drawer edge. */}
                    <div className="mt-1 flex w-full min-w-0 flex-wrap items-center justify-start gap-x-2 gap-y-1.5" data-testid="mobile-profile-actions">
                      <div className="flex min-w-0 max-w-full items-center">
                        {userRoles && userRoles.length > 0 ? (() => {
                          const activeMeta = ROLE_META[activeRole] || { label: (activeRole || user.role || 'Member').replace('_', ' '), Icon: User };
                          const ActiveRoleIcon = activeMeta.Icon;
                          return (
                            <button
                              type="button"
                              onClick={() => setRoleSheetOpen(true)}
                              data-testid="mobile-profile-role-trigger"
                              aria-label="Switch role"
                              aria-haspopup="dialog"
                              aria-expanded={roleSheetOpen}
                              title={`Current role: ${activeMeta.label}. Switch role`}
                              className="inline-flex h-9 min-[390px]:h-10 min-w-0 max-w-full items-center gap-1.5 rounded-lg bg-[#7A1DD8]/[0.08] px-2.5 min-[390px]:px-3 text-[#7A1DD8] ring-1 ring-inset ring-[#7A1DD8]/15 transition-colors duration-150 hover:bg-[#7A1DD8]/[0.14] active:bg-[#7A1DD8]/[0.20] dark:bg-[#7AA2FF]/[0.12] dark:text-[#B68BFF] dark:ring-[#7AA2FF]/20 dark:hover:bg-[#7AA2FF]/[0.18] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7A1DD8]/45"
                            >
                              <ActiveRoleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              <span
                                className="min-w-0 truncate text-[11px] min-[390px]:text-xs font-semibold capitalize"
                                data-testid="mobile-profile-active-role"
                              >
                                {activeMeta.label}
                              </span>
                              <ChevronDown className="h-3 w-3 flex-shrink-0 opacity-75" />
                            </button>
                          );
                        })() : (
                          <div className="inline-flex h-9 min-[390px]:h-10 min-w-0 max-w-full items-center gap-1.5 rounded-lg bg-[#7A1DD8]/[0.08] px-2.5 min-[390px]:px-3 text-[#7A1DD8] ring-1 ring-inset ring-[#7A1DD8]/15 dark:bg-[#7AA2FF]/[0.12] dark:text-[#B68BFF] dark:ring-[#7AA2FF]/20" data-testid="mobile-profile-active-role">
                            <User className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="min-w-0 truncate text-[11px] min-[390px]:text-xs font-semibold capitalize">
                              {user.role?.replace('_', ' ') || 'Member'}
                            </span>
                          </div>
                        )}
                      </div>

                      <Link
                        to="/dashboard?tab=profile"
                        onClick={closeMobile}
                        data-testid="mobile-profile-view-profile"
                        aria-label="View Profile"
                        className="group inline-flex h-9 min-[390px]:h-10 flex-shrink-0 items-center justify-center gap-1 rounded-lg bg-[#7A1DD8]/[0.05] dark:bg-[#7AA2FF]/[0.08] px-2.5 min-[390px]:px-3 text-[11px] min-[390px]:text-xs font-semibold text-[#7A1DD8] dark:text-[#B68BFF] transition-colors duration-150 hover:bg-[#7A1DD8]/[0.08] dark:hover:bg-[#7AA2FF]/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7A1DD8]/45 whitespace-nowrap"
                      >
                        <span>View Profile</span>
                        <svg className="h-3 w-3 flex-shrink-0 transition-transform duration-150 group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* (Removed) Older inline "Switch Role" card and the lower
                "Roles" accordion section. Mobile role switching now has
                a single entry point: the active-role pill inside the
                profile header above, which opens the premium bottom
                sheet rendered at the end of this drawer. */}

            {user && canShowPostCastingCta(activeRole) && (
              <div
                className="relative z-10 w-full max-w-full flex-shrink-0 overflow-hidden px-3 pb-2 pt-2 min-[390px]:px-4 sm:px-5"
                data-testid="mobile-post-casting-cta-wrap"
              >
                <Link
                  to={POST_CASTING_ROUTE}
                  onClick={() => {
                    trackPostCastingCta(
                      'mobile_menu',
                      activeRole,
                    );
                    closeMobile();
                  }}
                  className="flex min-h-12 w-full max-w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-theme-primary px-4 py-3 text-sm font-bold text-white shadow-md transition-[background-color,transform,box-shadow] duration-150 hover:bg-theme-primary-hover active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] focus-visible:ring-offset-2 focus-visible:ring-offset-theme"
                  data-testid="mobile-post-casting-cta"
                  aria-label="Post a Casting"
                >
                  <Plus
                    className="h-5 w-5 shrink-0"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 truncate">
                    Post a Casting
                  </span>
                </Link>
              </div>
            )}

            {/* Drawer search + quick-action tile grid (Phase 3 redesign).
                Sits between the profile card and the grouped menu sections.
                - Search filters menu items by label across all sections.
                  Active accordions auto-expand while a query is present.
                - Quick action tiles surface the 6 priority actions
                  (Dashboard, Casting Calls, Network, Talent Directory,
                  Messages, Notifications) using the existing item icons/routes
                  from navigation.js.
                  Tiles hide while the user is searching. */}
            {user && (() => {
              const sections = getMobileDrawer(user);
              const flatItems = sections.flatMap((s) => s.items.filter((it) => !it.divider));
              const findItem = (id) => flatItems.find((it) => it.id === id);
              // Wayne sign-off adjustment:
              // Dashboard, Casting Calls and Network should be the first
              // actions users see in the app drawer. These all come from
              // the shared navigation config, so no duplicate routes are created.
              const preferred = ['home', 'casting-calls', 'network', 'talent', 'messages', 'notifications'];
              const fallbacks = ['saved', 'planner', 'edit-profile']; // safe, role-aware backfill
              const seen = new Set();
              const quickTiles = [];
              for (const id of preferred) {
                const it = findItem(id);
                if (it && !seen.has(it.id)) { quickTiles.push(it); seen.add(it.id); }
              }
              if (quickTiles.length < 6) {
                for (const fid of fallbacks) {
                  if (quickTiles.length >= 6) break;
                  const it = findItem(fid);
                  if (it && !seen.has(it.id)) { quickTiles.push(it); seen.add(it.id); }
                }
              }
              const finalTiles = quickTiles.slice(0, 6);
              const badgeMap = { messages: messageUnread, notifications: unreadNotifications };
              const searching = drawerSearch.trim().length > 0;

              return (
                <div className="relative z-10 px-3 min-[390px]:px-4 sm:px-5 pt-0 pb-3 flex-shrink-0 border-b border-theme/15 !rounded-none !border-x-0 !border-t-0 !bg-transparent !shadow-none !ring-0">
                  {/* Search and shortcuts sit on the drawer surface, without a shared card. */}
                  <div className="px-1 min-[390px]:px-1.5 pt-1.5 min-[390px]:pt-2 pb-3 !rounded-none !border-0 !bg-transparent !shadow-none !ring-0">
                    {/* Search pill */}
                    <label className="block relative" data-testid="mobile-drawer-search-wrap">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500 pointer-events-none" />
                    <input
                      type="search"
                      value={drawerSearch}
                      onChange={(e) => setDrawerSearch(e.target.value)}
                      placeholder="Search menu, tools, people…"
                      className="w-full h-9 min-[390px]:h-10 pl-9 pr-9 rounded-full text-[12.5px] min-[390px]:text-[13.5px] font-medium text-theme-text placeholder:text-slate-400 dark:placeholder:text-slate-500 bg-theme-surface/80 dark:bg-white/[0.04] border border-theme/20 focus:border-[#7A1DD8]/45 focus:bg-theme-surface focus:outline-none focus:ring-2 focus:ring-[#7A1DD8]/20 transition-colors"
                      data-testid="mobile-drawer-search"
                      aria-label="Search menu"
                    />
                    {searching && (
                      <button
                        type="button"
                        onClick={() => setDrawerSearch('')}
                        aria-label="Clear search"
                        data-testid="mobile-drawer-search-clear"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-slate-400 dark:text-slate-500 hover:text-theme-text hover:bg-theme/10 transition-colors flex items-center justify-center"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </label>

                  {/* Quick action tile grid — hidden while searching so
                      the user focuses on the filtered results below.
                      Always renders a clean 3-col grid; missing routes
                      back-filled with `home` so we never leave gaps. */}
                  {!searching && finalTiles.length > 0 && (
                    <div
                      className="mt-2.5 min-[390px]:mt-3 grid grid-cols-3 gap-2 min-[390px]:gap-2.5"
                      data-testid="mobile-drawer-quick-tiles" data-design-version="mobile-quick-tiles-v12"
                    >
                      {finalTiles.map((tile) => {
                        const TileIcon = tile.icon || User;
                        const badge = tile.badgeKey ? badgeMap[tile.badgeKey] : 0;
                        return (
                          <Link
                            key={tile.id}
                            to={tile.to}
                            onClick={closeMobile}
                            data-testid={`mobile-quick-tile-${tile.id}`}
                            className="group relative flex flex-col items-center justify-center gap-1 min-[390px]:gap-1.5 rounded-[10px] min-[390px]:rounded-xl min-h-[72px] min-[390px]:min-h-[78px] px-1.5 min-[390px]:px-2 py-[9px] min-[390px]:py-[11px] bg-gradient-to-br from-[#7A1DD8]/[0.08] via-[#A56CEC]/[0.05] to-[#002FA7]/[0.06] dark:from-[#7AA2FF]/[0.10] dark:via-[#B68BFF]/[0.07] dark:to-[#0055FF]/[0.10] border border-[#7A1DD8]/15 dark:border-[#7AA2FF]/15 hover:border-[#7A1DD8]/40 dark:hover:border-[#7AA2FF]/40 hover:from-[#7A1DD8]/[0.14] hover:via-[#A56CEC]/[0.08] hover:to-[#002FA7]/[0.10] active:scale-[0.97] transition-[transform,background,border-color] duration-150 shadow-[0_10px_28px_-22px_rgba(122,29,216,0.65)]"
                          >
                            <span className="inline-flex h-10 w-10 min-[390px]:h-11 min-[390px]:w-11 items-center justify-center rounded-lg bg-white/90 dark:bg-white/[0.07] ring-1 ring-[#7A1DD8]/14 dark:ring-[#7AA2FF]/18 text-[#7A1DD8] dark:text-[#B68BFF] shadow-sm group-hover:shadow-md transition-shadow">
                              <TileIcon className="h-4 w-4 min-[390px]:h-5 min-[390px]:w-5" />
                            </span>
                            <span className="text-[10.5px] min-[390px]:text-[11.5px] font-semibold text-theme-text text-center leading-tight line-clamp-1">
                              {tile.label}
                            </span>
                            {badge > 0 && (
                              <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-white dark:ring-[#0B1120]">
                                {badge > 9 ? '9+' : badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                  </div>
                </div>
              );
            })()}

            {/* Authenticated drawer body — driven by `getMobileDrawer(user)`
                from /app/frontend/src/config/navigation.js (same single
                source of truth as the desktop sidebar via
                `getDesktopSidebar`). Sections now mirror the desktop menu:
                  • Quick tiles above remain shortcuts only
                  • Discover              (accordion, collapsed by default)
                  • My Dashboard          (accordion, includes role toolkit rows)
                  • Site Management       (accordion, admin/staff only)
                  • Account               (accordion)
                  • Help & Support        (accordion — Support Centre, My Tickets, Show help, Report an issue)
                  • Legal                 (accordion)
                Permissions are NOT re-evaluated here — `getDesktopSidebar`
                already gates the admin section via `isAdminOrStaff` and
                drops empty sections, so this drawer can never expose
                items the user isn't permitted to see. */}
            {user && (() => {
              // Render the full role-aware drawer section list. My Hub is
              // intentionally kept so Dashboard/My Dashboard can behave as
              // the first collapsible drawer section.
              const mobileSectionOrder = ['my-hub', 'discover', 'site-mgmt', 'account', 'help', 'legal'];
              const mobileSectionsRaw = getMobileDrawer(user)
                .map((s) => (
                  s.id === 'my-hub'
                    ? { ...s, title: 'My Dashboard', collapsible: true, defaultOpen: false }
                    : s
                ))
                .sort((a, b) => {
                  const ai = mobileSectionOrder.indexOf(a.id);
                  const bi = mobileSectionOrder.indexOf(b.id);
                  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                });
              const badgeMap = { messages: messageUnread, notifications: unreadNotifications };

              // Phase 3 — apply the drawer search filter. While searching
              // we drop non-matching items and empty sections, and force
              // every remaining section open so matches are immediately
              // visible (no extra tap to expand).
              const q = drawerSearch.trim().toLowerCase();
              const searching = q.length > 0;
              const mobileSections = searching
                ? mobileSectionsRaw
                    .map((s) => ({
                      ...s,
                      items: s.items.filter(
                        (it) => !it.divider && (it.label || '').toLowerCase().includes(q)
                      ),
                    }))
                    .filter((s) => s.items.length > 0)
                : mobileSectionsRaw;

              const renderItemRow = (item, sectionId) => {
                // Divider/header item — renders as a tiny uppercase
                // label inside the section. Used by the Admin Site
                // Management section to group its items into People /
                // Operations / Settings without forcing a nested
                // accordion. Keeps the existing flat item shape.
                if (item.divider) {
                  return (
                    <div
                      key={item.id}
                      className="px-3 pt-3 pb-1.5 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#7A1DD8]/75 dark:text-[#B68BFF]/80 select-none"
                      data-testid={`mobile-${sectionId}-divider-${item.id}`}
                    >
                      {item.label}
                    </div>
                  );
                }
                const [toPath, toQuery = ''] = (item.to || '').split('?');
                const toTab = new URLSearchParams(toQuery).get('tab');
                const currentTab = new URLSearchParams(location.search).get('tab');
                const active = toTab
                  ? location.pathname === toPath && currentTab === toTab
                  : isActive(toPath);
                const Icon = item.icon || User;
                const badge = item.badgeKey ? badgeMap[item.badgeKey] : 0;
                return (
                  <Link
                    key={item.id}
                    to={item.to}
                    onClick={closeMobile}
                    aria-current={active ? 'page' : undefined}
                    data-testid={`mobile-${sectionId}-${item.id}`}
                    className={`group flex items-center gap-3 px-3 py-2.5 min-h-[42px] text-[14px] font-semibold rounded-xl transition-[background-color,box-shadow,color] duration-150 ${
                      active
                        ? 'bg-[#002FA7]/10 text-[#002FA7] dark:bg-[#0055FF]/15 dark:text-[#7AA2FF] shadow-[inset_2px_0_0_0_#002FA7] dark:shadow-[inset_2px_0_0_0_#7AA2FF]'
                        : 'text-slate-800 dark:text-slate-100 hover:bg-[#002FA7]/[0.06] dark:hover:bg-[#0055FF]/[0.08] hover:text-[#002FA7] dark:hover:text-[#7AA2FF]'
                    }`}
                  >
                    <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${active ? 'text-[#002FA7] dark:text-[#7AA2FF]' : 'text-slate-500 dark:text-slate-400 group-hover:text-[#002FA7] dark:group-hover:text-[#7AA2FF]'}`} />
                    <span className="truncate">{item.label}</span>
                    {badge > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                        {badge > 9 ? '9+' : badge}
                      </span>
                    )}
                  </Link>
                );
              };

              return (
                <nav className="px-3 min-[390px]:px-4 sm:px-5 pt-1.5 min-[390px]:pt-2 pb-4 flex-1 min-h-0 overflow-y-auto overscroll-contain" aria-label="Drawer menu">
                  {searching && mobileSections.length === 0 && (
                    <div
                      className="px-4 py-10 text-center text-[13px] text-theme-muted"
                      data-testid="mobile-drawer-search-empty"
                    >
                      No menu items match <span className="font-semibold text-theme-text">&ldquo;{drawerSearch.trim()}&rdquo;</span>
                    </div>
                  )}
                  {mobileSections.map((section) => {
                    // Help & Support is special-cased: it includes two
                    // imperative actions (Show help, Report an issue)
                    // alongside the routed links from navigation.js.
                    const isHelp = section.id === 'help';

                    if (!section.collapsible && section.id !== 'my-hub') {
                      // Always-on section fallback for any future non-collapsible
                      // section. My Hub is promoted to a collapsible Dashboard
                      // drawer above.
                      return (
                        <div
                          className="mb-3 rounded-2xl border border-[#7A1DD8]/12 dark:border-[#7AA2FF]/12 bg-white/65 dark:bg-white/[0.035] p-2 shadow-[0_10px_30px_-24px_rgba(122,29,216,0.45)]"
                          data-testid={`mobile-${section.id}-section`}
                          key={section.id}
                        >
                          <h4 className="px-2 mb-1.5 text-[11px] font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-[0.16em]">
                            {section.title}
                          </h4>
                          <div className="space-y-0.5">
                            {section.items.map((item) => renderItemRow(item, section.id))}
                          </div>
                        </div>
                      );
                    }

                    // Collapsible accordion section. While searching every
                    // matching section is forced open so users can see
                    // results without an extra tap. All normal drawer
                    // sections use the same accordion behaviour, including
                    // My Hub/Dashboard.
                    const sectionOpen = searching || mobileOpenSections.has(section.id);
                    return (
                      <DrawerSection
                        key={section.id}
                        id={`mobile-${section.id}`}
                        title={section.title}
                        open={sectionOpen}
                        onToggle={() => {
                          if (searching) return; // search forces them open
                          setMobileOpenSections((prev) => {
                            // Single-open accordion: clicking a closed section
                            // opens it and closes all others; clicking the
                            // currently open section collapses it.
                            if (prev.has(section.id)) return new Set();
                            return new Set([section.id]);
                          });
                        }}
                      >
                        <div className="space-y-0.5">
                          {section.items.map((item) => renderItemRow(item, section.id))}
                          {isHelp && !searching && (
                            <>
                              <button
                                type="button"
                                onClick={() => { handleReplayHelp(); closeMobile(); }}
                                className="group flex items-center gap-3 w-full px-3 py-2.5 text-[14px] font-medium rounded-lg text-slate-800 dark:text-slate-100 hover:bg-[#002FA7]/[0.06] dark:hover:bg-[#0055FF]/[0.08] hover:text-[#002FA7] dark:hover:text-[#7AA2FF] transition-[background-color] duration-150"
                                data-testid="mobile-help-show-help"
                              >
                                <HelpCircle className="h-[18px] w-[18px] flex-shrink-0 text-slate-500 dark:text-slate-400 group-hover:text-[#002FA7] dark:group-hover:text-[#7AA2FF]" />
                                <span className="truncate">Show help</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setShowReportIssue(true); closeMobile(); }}
                                className="group flex items-center gap-3 w-full px-3 py-2.5 text-[14px] font-medium rounded-lg text-slate-800 dark:text-slate-100 hover:bg-[#002FA7]/[0.06] dark:hover:bg-[#0055FF]/[0.08] hover:text-[#002FA7] dark:hover:text-[#7AA2FF] transition-[background-color] duration-150"
                                data-testid="mobile-help-report-issue"
                              >
                                <LifeBuoy className="h-[18px] w-[18px] flex-shrink-0 text-slate-500 dark:text-slate-400 group-hover:text-[#002FA7] dark:group-hover:text-[#7AA2FF]" />
                                <span className="truncate">Report an issue</span>
                              </button>
                            </>
                          )}
                        </div>
                      </DrawerSection>
                    );
                  })}
                </nav>
              );
            })()}

            {/* Logged-out public drawer — grouped marketing/site navigation.
                Signed-in users keep the role-aware getMobileDrawer(user) flow above. */}
            {!user && (
              <>
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#002FA7] via-[#7A1DD8] to-[#002FA7]" />

                <div className="flex-shrink-0 px-5 pt-[max(1.2rem,env(safe-area-inset-top))] pb-4 border-b border-theme/20">
                  <button
                    onClick={closeMobile}
                    className="absolute top-[max(0.8rem,env(safe-area-inset-top))] right-4 h-10 w-10 rounded-xl text-theme-muted hover:text-[#002FA7] dark:hover:text-[#7AA2FF] hover:bg-[#002FA7]/[0.06] dark:hover:bg-[#0055FF]/[0.10] active:scale-95 transition-[background-color,color,transform] duration-200 ease-out flex items-center justify-center"
                    aria-label="Close menu"
                    data-testid="mobile-public-menu-close"
                  >
                    <X className="h-5 w-5" />
                  </button>

                  <Link
                    to="/"
                    onClick={closeMobile}
                    className="inline-flex items-center pr-12"
                    data-testid="mobile-public-logo"
                    aria-label={mobileSiteName}
                  >
                    <SiteLogo variant="mobile" showTagline={false} />
                  </Link>

                  <p className="mt-4 max-w-[18rem] text-[13px] leading-5 text-theme-muted">
                    The UK network connecting talent, casting professionals, agents, production companies and creative industry services.
                  </p>
                </div>

                <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pt-5 pb-20" aria-label="Public navigation">
                  <div className="space-y-5">
                    {PUBLIC_MOBILE_NAV_GROUPS.map((section) => (
                      <section key={section.id} data-testid={`mobile-public-${section.id}-section`}>
                        <h4 className="px-1 mb-2 text-[11.5px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-[0.14em]">
                          {section.title}
                        </h4>

                        <div className="space-y-1">
                          {section.items.map((item) => {
                            const Icon = item.icon || User;
                            const active = isActive(item.path);

                            return (
                              <Link
                                key={item.path}
                                to={item.path}
                                onClick={closeMobile}
                                aria-current={active ? 'page' : undefined}
                                className={`group flex min-h-[44px] items-center gap-3 rounded-xl px-3.5 py-2 text-[14.5px] font-semibold transition-[background-color,box-shadow,color,transform] duration-150 ${
                                  active
                                    ? 'bg-[#002FA7]/10 text-[#002FA7] dark:bg-[#0055FF]/15 dark:text-[#7AA2FF] shadow-[inset_2px_0_0_0_#002FA7] dark:shadow-[inset_2px_0_0_0_#7AA2FF]'
                                    : 'text-theme-text hover:bg-[#002FA7]/[0.06] dark:hover:bg-[#0055FF]/[0.08] hover:text-[#002FA7] dark:hover:text-[#7AA2FF]'
                                }`}
                                data-testid={`mobile-public-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <span
                                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl border transition-colors ${
                                    active
                                      ? 'border-[#002FA7]/20 bg-[#002FA7]/10 text-[#002FA7] dark:border-[#7AA2FF]/25 dark:bg-[#0055FF]/15 dark:text-[#7AA2FF]'
                                      : 'border-theme/40 bg-theme-surface/60 text-theme-muted group-hover:border-[#002FA7]/20 group-hover:bg-[#002FA7]/10 group-hover:text-[#002FA7] dark:group-hover:text-[#7AA2FF]'
                                  }`}
                                >
                                  <Icon className="h-4 w-4" />
                                </span>
                                <span className="truncate">{item.label}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>
                </nav>
              </>
            )}

            {/* D. Footer — compact one-line utility bar.
                Matches the mockup direction: theme control on the left,
                logout on the right, no bulky stacked footer. */}
            <div className="border-t border-theme/20 px-3 min-[390px]:px-4 pt-2 pb-[max(0.55rem,env(safe-area-inset-bottom))] min-[390px]:pb-[max(0.7rem,env(safe-area-inset-bottom))] flex-shrink-0 bg-white/88 dark:bg-[#0B1120]/88 backdrop-blur-xl shadow-[0_-18px_40px_-32px_rgba(15,23,42,0.6)]">
              {user ? (
                <div className="flex items-center justify-between gap-1.5 min-[390px]:gap-2 rounded-2xl border border-[#7A1DD8]/10 dark:border-[#7AA2FF]/10 bg-white/72 dark:bg-white/[0.035] px-2 min-[390px]:px-2.5 py-1.5 min-[390px]:py-2 shadow-[0_10px_28px_-24px_rgba(122,29,216,0.55)]">
                  <button
                    type="button"
                    onClick={handleMobileThemeToggle}
                    data-testid="mobile-dark-mode-toggle"
                    aria-pressed={themePreference === 'dark'}
                    aria-label="Toggle dark mode"
                    className="flex min-w-0 flex-1 items-center gap-1.5 min-[390px]:gap-2 rounded-xl px-1.5 min-[390px]:px-2 py-1.5 min-[390px]:py-2 text-[11px] min-[390px]:text-xs font-semibold text-theme-text-secondary hover:bg-[#7A1DD8]/[0.05] dark:hover:bg-[#7AA2FF]/[0.08] transition-colors"
                  >
                    <Moon className="h-4 w-4 flex-shrink-0 text-[#7A1DD8] dark:text-[#B68BFF]" />
                    <span className="truncate">{themePreference === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                    <span
                      className={`relative ml-auto inline-flex h-5 w-9 flex-shrink-0 rounded-full border shadow-inner transition-colors duration-200 ${
                        themePreference === 'dark'
                          ? 'bg-[#7A1DD8] border-[#7A1DD8]'
                          : 'bg-slate-300 border-slate-400/70'
                      }`}
                      data-testid="mobile-dark-mode-switch"
                    >
                      <span
                        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white border border-slate-300 shadow-md transition-transform duration-200 ${
                          themePreference === 'dark'
                            ? 'translate-x-[18px]'
                            : 'translate-x-0.5'
                        }`}
                      />
                    </span>
                  </button>

                  <span className="h-6 w-px bg-theme/15 dark:bg-white/10" aria-hidden="true" />

                  <button
                    onClick={() => { handleLogout(); closeMobile(); }}
                    data-testid="mobile-logout-button"
                    className="flex flex-shrink-0 items-center gap-1 rounded-xl px-2 min-[390px]:px-2.5 py-1.5 min-[390px]:py-2 text-[11px] min-[390px]:text-xs font-bold text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Log Out</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-2 pt-2 px-1">
                  <Link to="/register" onClick={closeMobile}>
                    <Button
                      className="w-full bg-[#002FA7] hover:bg-[#0055FF] text-white h-11 text-[14px] font-semibold rounded-lg shadow-sm transition-all duration-200"
                      data-testid="mobile-public-join-now"
                    >
                      Join Now
                    </Button>
                  </Link>
                  <Link to="/login" onClick={closeMobile}>
                    <Button
                      variant="outline"
                      className="w-full border-theme text-theme-text hover:text-[#002FA7] hover:bg-[#002FA7]/5 hover:border-[#002FA7]/40 dark:hover:text-[#7AA2FF] h-11 text-[14px] font-semibold rounded-lg transition-all duration-200"
                      data-testid="mobile-public-sign-in"
                    >
                      Sign In
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Premium Mobile Role Bottom Sheet — only entry point for
            role switching on mobile. Rendered at fragment level so it
            stacks above the drawer (z-[180]) and is unaffected by the
            drawer's translate transform. Reuses `userRoles`,
            `activeRole`, `switchRole`, `ROLE_META` from AuthContext
            and the existing Add-Role flow
            (`/dashboard` + `state.openRoleModal`). */}
        {user && userRoles && userRoles.length > 0 && (
          <>
            {/* Backdrop */}
            <div
              className={`xl:hidden fixed inset-0 bg-black/60 dark:bg-black/70 z-[170] transition-opacity duration-300 ${
                roleSheetOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              onClick={() => setRoleSheetOpen(false)}
              data-testid="mobile-role-sheet-backdrop"
              aria-hidden="true"
            />

            {/* Sheet panel */}
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Switch role"
              data-testid="mobile-role-sheet"
              className={`xl:hidden fixed left-0 right-0 bottom-0 z-[180] bg-white dark:bg-[#0D1528] rounded-t-3xl shadow-2xl border-t border-theme/10 transform transition-transform duration-300 ease-out ${
                roleSheetOpen ? 'translate-y-0' : 'translate-y-full'
              }`}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1.5 rounded-full bg-theme/30 dark:bg-white/20" />
              </div>

              {/* Header */}
              <div className="px-5 pt-2 pb-3 border-b border-theme/10">
                <h3 className="text-[17px] font-bold text-theme-text tracking-tight" data-testid="mobile-role-sheet-title">
                  {userRoles.length > 1 ? 'Switch role' : 'Your role'}
                </h3>
                <p className="text-[12.5px] text-theme-muted mt-0.5">
                  {userRoles.length > 1
                    ? 'Choose the role you want to act as.'
                    : 'You currently have one role on your account.'}
                </p>
              </div>

              {/* Role list */}
              <div className="max-h-[55vh] overflow-y-auto py-1.5" data-testid="mobile-role-sheet-list">
                {userRoles.map((role) => {
                  const meta = ROLE_META[role] || { label: role, Icon: User };
                  const RoleIcon = meta.Icon;
                  const isActiveRole = role === activeRole;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={async () => {
                        if (isActiveRole) {
                          setRoleSheetOpen(false);
                          return;
                        }
                        try {
                          await switchRole(role);
                        } catch {
                          /* AuthContext.switchRole surfaces its own toast */
                        }
                        setRoleSheetOpen(false);
                      }}
                      data-testid={`mobile-role-sheet-option-${role}`}
                      className={`w-full flex items-center gap-4 px-5 py-3.5 text-left transition-colors ${
                        isActiveRole
                          ? 'bg-[#002FA7]/5 dark:bg-[#0055FF]/10'
                          : 'hover:bg-[#002FA7]/[0.04] dark:hover:bg-[#0055FF]/[0.06] active:bg-[#002FA7]/[0.10]'
                      }`}
                    >
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          isActiveRole
                            ? 'bg-gradient-to-br from-[#002FA7] to-[#0055FF] text-white shadow-md'
                            : 'bg-theme-surface text-theme-muted border border-theme/30'
                        }`}
                      >
                        <RoleIcon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-[15px] font-semibold truncate ${
                            isActiveRole ? 'text-[#002FA7] dark:text-[#7AA2FF]' : 'text-theme-text'
                          }`}
                        >
                          {meta.label}
                        </div>
                        {isActiveRole && (
                          <div className="text-[11px] text-theme-muted mt-0.5">Active</div>
                        )}
                      </div>
                      {isActiveRole && (
                        <Check className="h-5 w-5 text-[#002FA7] dark:text-[#7AA2FF] flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Add another role — uses existing Add Role flow */}
              <div className="border-t border-theme/10 px-3 py-2">
                <button
                  type="button"
                  data-testid="mobile-role-sheet-add"
                  onClick={() => {
                    setRoleSheetOpen(false);
                    closeMobile();
                    navigate('/dashboard', { state: { openRoleModal: true } });
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[14.5px] font-semibold text-[#002FA7] dark:text-[#7AA2FF] hover:bg-[#002FA7]/[0.06] dark:hover:bg-[#0055FF]/[0.08] active:bg-[#002FA7]/[0.10] transition-colors"
                >
                  <div className="h-9 w-9 rounded-full bg-[#002FA7]/10 dark:bg-[#0055FF]/15 flex items-center justify-center flex-shrink-0">
                    <Plus className="h-5 w-5" />
                  </div>
                  <span>Add another role</span>
                </button>
              </div>

              {/* Cancel + safe-area inset for iOS home indicator */}
              <div className="px-3 pt-1 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <button
                  type="button"
                  data-testid="mobile-role-sheet-cancel"
                  onClick={() => setRoleSheetOpen(false)}
                  className="w-full px-3 py-3 rounded-xl text-[14px] font-medium text-theme-muted hover:bg-theme-surface/60 dark:hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}
      </>
      {/* Report an issue modal */}
      {showReportIssue && <ReportIssueModal onClose={() => setShowReportIssue(false)} />}
    </>
  );
}

function CookieFooterTab() {
  return (
    <button
      type="button"
      onClick={() => {
        window.dispatchEvent(
          new Event('ccuk:open-cookie-settings'),
        );
      }}
      className="
        absolute left-4 sm:left-[4.375rem] top-0
        -translate-y-[55%]
        z-0
        inline-flex h-7 items-center gap-1.5
        rounded-t-lg border border-b-0 border-theme
        bg-theme-secondary px-2.5
        text-[10px] font-semibold text-theme-muted
        shadow-sm
        transition
        hover:text-theme-text hover:bg-theme-surface
        focus:outline-none focus:ring-2 focus:ring-[#0055FF]
      "
      aria-label="Open cookie settings"
      data-testid="cookie-footer-settings-button"
      title="Cookie settings"
    >
      <Cookie className="h-3.5 w-3.5" aria-hidden="true" />
      <span>Cookies</span>
    </button>
  );
}

export function Footer() {
  const { branding, brandingLoaded } = useBranding();
  const { user } = useAuth();

  const platformLinks = [
    { label: 'Talent Directory', path: '/talent' },
    { label: 'Casting Professionals', path: '/casting-professionals' },
    { label: 'Casting Calls', path: '/casting-calls' },
    { label: 'Industry Services', path: '/services' },
    { label: 'Agents', path: '/agents' },
    { label: 'Production Companies', path: '/companies' },
    { label: 'Events', path: '/events' },
    { label: 'News', path: '/news' },
  ];

  const companyLinks = [
    { label: 'About', path: '/about' },
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Contact', path: '/contact' },
    { label: 'Support', path: '/support' },
  ];

  const legalLinks = [
    { label: 'Terms of Service', path: '/terms' },
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Cookie Policy', path: '/cookie-policy' },
    { label: 'Community Guidelines', path: '/guidelines' },
  ];

  // Logged-in app shell only needs a compact status/copyright strip — the
  // full marketing footer (Resources / Company / Legal columns) is a
  // public-pages-only construct. Legal links are pinned to the sidebar
  // for signed-in users, so we don't lose access to them.
  if (user) {
    return (
      <footer className="relative bg-theme-secondary border-t border-theme" data-testid="footer-compact">
        <CookieFooterTab />
        <div className="relative z-10 bg-theme-secondary max-w-screen-2xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-theme-muted text-xs sm:text-sm" data-testid="footer-copyright">
            {branding.footer_copyright || `© ${new Date().getFullYear()} ${branding.site_name || 'Casting Calls UK'} LTD. All rights reserved.`}
          </p>
          <div className="flex items-center gap-4">
            <FooterStatusBadge />
            <span className="text-theme-muted text-xs sm:text-sm hidden lg:inline">Made with passion for the UK creative industry</span>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="relative bg-theme-secondary border-t border-theme" data-testid="footer">
      <CookieFooterTab />
      <div className="relative z-10 bg-theme-secondary max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center mb-4" data-testid="footer-logo-link">
              <SiteLogo variant="footer" />
            </Link>
            {branding.footer_description && (
              <p className="text-theme-muted text-sm leading-relaxed" data-testid="footer-description">
                {branding.footer_description}
              </p>
            )}
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-heading font-semibold text-theme-text mb-4">Resources</h4>
            <ul className="space-y-2">
              {platformLinks.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-theme-muted hover:text-theme-text text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading font-semibold text-theme-text mb-4">Company</h4>
            <ul className="space-y-2">
              {companyLinks.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-theme-muted hover:text-theme-text text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-heading font-semibold text-theme-text mb-4">Legal</h4>
            <ul className="space-y-2">
              {legalLinks.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-theme-muted hover:text-theme-text text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-theme flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-theme-muted text-sm" data-testid="footer-copyright">
            {branding.footer_copyright || `© ${new Date().getFullYear()} ${branding.site_name || 'Casting Calls UK'}. All rights reserved.`}
          </p>
          <div className="flex items-center gap-4">
            <FooterStatusBadge />
            <span className="text-theme-muted text-sm hidden lg:inline">Made with passion for the UK creative industry</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterStatusBadge() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const API_URL = window.__BACKEND_URL__;
        const axios = (await import('axios')).default;
        const { data } = await axios.get(`${API_URL}/api/status/overview`);
        setStatus(data.overall);
      } catch {
        /* silent */
      }
    };
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);

  if (!status) return null;
  const meta = {
    operational: { label: 'All systems operational', dot: 'bg-green-500' },
    degraded: { label: 'Degraded performance', dot: 'bg-amber-500' },
    partial_outage: { label: 'Partial outage', dot: 'bg-orange-500' },
    major_outage: { label: 'Major outage', dot: 'bg-red-500' },
  }[status] || { label: 'Status unavailable', dot: 'bg-theme/60' };

  return (
    <Link
      to="/system-status"
      className="inline-flex items-center gap-2 text-xs text-theme-muted hover:text-theme-text transition-colors"
      data-testid="footer-status-link"
    >
      <span className={`h-2 w-2 rounded-full ${meta.dot} ${status !== 'operational' ? 'animate-pulse' : ''}`} />
      {meta.label}s
    </Link>
  );
}


